import { createFileRoute } from "@tanstack/react-router";
import { ListChecks, Search } from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";

import { CompletionStatusFilter } from "~/components/completion-status-filter";
import { ActionCardItem } from "~/components/engagement/action-card/action-card-item";
import { EmptyState } from "~/components/ui/empty-state";
import { Skeleton } from "~/components/ui/skeleton";
import { useCoachInfoForEngagement } from "~/lib/queries/engagementQuery";
import { useHomeworkList } from "~/lib/queries/homeworkQuery";
import { useToggleTaskStatus } from "~/lib/queries/taskQuery";
import type { CompletionStatus } from "~/lib/schemas/common";
import { TaskStatus } from "~/lib/schemas/db-enums";
import { parseHomeworkStatus } from "~/lib/utils/homework-status-validation";

import { homeworkToActionViewModel } from "./-components/homework-to-action-view-model";
import { UserHomeworkPreviewDrawer } from "./-components/user-homework-preview-drawer";

export const Route = createFileRoute("/_user/coaching/$engagementId/homework")({
  staticData: { title: "Actions" },
  component: HomeworkPage,
});

function HomeworkPage() {
  const { engagementId } = Route.useParams();
  const [statusFilter, setStatusFilter] = useState<CompletionStatus>("all");
  const [selectedHomeworkId, setSelectedHomeworkId] = useState<string | null>(
    null,
  );

  const { data, isLoading } = useHomeworkList(engagementId);
  const { data: coachInfo } = useCoachInfoForEngagement(engagementId);
  const updateStatusMutation = useToggleTaskStatus();

  const homeworks = data?.homeworks ?? [];

  const filteredHomeworks = useMemo(() => {
    return homeworks.filter((hw) => {
      if (statusFilter === "all") return true;
      const parsed = parseHomeworkStatus(hw.status);
      if (statusFilter === "in_progress") return parsed === TaskStatus.ACTIVE;
      if (statusFilter === "completed") return parsed === TaskStatus.DONE;
      return true;
    });
  }, [homeworks, statusFilter]);

  const selectedHomework = homeworks.find((hw) => hw.id === selectedHomeworkId);

  const handleStatusChange = (homework: { id: string }) => {
    const current = homeworks.find((hw) => hw.id === homework.id);
    const newStatus =
      current?.status === TaskStatus.DONE ? TaskStatus.ACTIVE : TaskStatus.DONE;

    updateStatusMutation.mutate(homework.id, {
      onSuccess: () => {
        toast.success(
          newStatus === TaskStatus.DONE
            ? "Homework marked as complete"
            : "Homework marked as active",
        );
      },
      onError: () => {
        toast.error("Failed to update homework status");
      },
    });
  };

  const transformForPreview = (hw: (typeof homeworks)[number] | undefined) => {
    if (!hw) return null;
    return {
      id: hw.id,
      title: hw.title,
      description: hw.description,
      deadline: hw.deadline,
      priority: hw.priority,
      status: parseHomeworkStatus(hw.status),
      trigger: hw.trigger,
      sessionName: hw.session?.title ?? null,
      coachName: coachInfo?.name ?? null,
      coachProfilePictureUrl: coachInfo?.profilePictureUrl ?? null,
    };
  };

  if (isLoading) {
    return (
      <div className="flex w-full min-w-0 flex-1 flex-col space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-border p-4">
            <div className="flex items-start gap-3">
              <Skeleton className="size-5 rounded-md" />
              <div className="flex-1">
                <Skeleton className="mb-2 h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (homeworks.length === 0) {
    return (
      <EmptyState
        size="lg"
        icon={ListChecks}
        title="No actions yet"
        description="Tasks your coach assigns will show up here."
      />
    );
  }

  return (
    <>
      <div className="flex w-full min-w-0 flex-1 flex-col space-y-3">
        <div className="flex items-center">
          <CompletionStatusFilter
            currentFilter={statusFilter}
            onFilterChange={setStatusFilter}
          />
        </div>

        {filteredHomeworks.length === 0 ? (
          <EmptyState
            size="sm"
            icon={Search}
            title="No matches"
            description="Try adjusting your filters."
          />
        ) : (
          filteredHomeworks.map((homework, index) => (
            <div
              key={homework.id}
              className="cursor-pointer"
              onClick={() => setSelectedHomeworkId(homework.id)}
            >
              <ActionCardItem
                item={homeworkToActionViewModel(
                  homework,
                  coachInfo ?? undefined,
                  engagementId,
                )}
                density="full"
                order={index}
                decryptGoal
                onToggleStatus={() => handleStatusChange(homework)}
              />
            </div>
          ))
        )}
      </div>

      <UserHomeworkPreviewDrawer
        open={!!selectedHomeworkId}
        onOpenChange={(open) => {
          if (!open) setSelectedHomeworkId(null);
        }}
        onStatusChange={handleStatusChange}
        homework={transformForPreview(selectedHomework)}
      />
    </>
  );
}
